import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MuseumRoundedIcon from '@mui/icons-material/MuseumRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import LuggageRoundedIcon from '@mui/icons-material/LuggageRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { PlannerLayout } from '../components/layout/PlannerLayout';
import { TripOverviewHero } from '../components/planner/TripOverviewHero';
import { ExperienceSection } from '../components/planner/ExperienceSection';
import { SpecialActivitiesSection } from '../components/planner/SpecialActivitiesSection';
import { ExperienceDetailDrawer } from '../components/planner/ExperienceDetailDrawer';
import { usePlanner } from '../contexts/PlannerContext';
import { useParams, useNavigate } from 'react-router-dom';
import { restoreSearch } from '../services/searchHistoryApi';
import { addDayActivities, addRestaurants, generateNewDay, deleteActivity, moveActivity, saveTrip } from '../services/travelApi';
import { PlannerMapPanel } from '../components/planner/PlannerMapPanel';
import { WeatherSummaryCard } from '../components/planner/WeatherSummaryCard';
import { MultiCityRouteNotes } from '../components/planner/MultiCityRouteNotes';
import { TopLoadingIndicator, LoadingState } from '../components/planner/TopLoadingIndicator';
import { PlannerLoadingPlaceholder } from '../components/planner/PlannerLoadingPlaceholder';
import { MarkdownText } from '../components/common/MarkdownText';
import { emphasizeMarkdownLead } from '../utils/markdown';
import { ShareTripButton } from '../components/common/ShareTripButton';
import { SaveTripButton } from '../components/common/SaveTripButton';
import { PlannerSection } from '../types/planner';
import { surfaceBackgroundSx } from '../theme/backgrounds';
import { PERIPLO_URL, PERIPLO_BRAND } from '../constants/periplo';
import periploIcon from '../periplo_icon.png';

type PlannerResultsPageProps = {
  disableAutoRestore?: boolean;
};

const InfoCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => {
  const theme = useTheme();
  const borderTint = alpha(theme.palette.primary.main, 0.12);
  const hoverGlow = alpha(theme.palette.primary.main, 0.14);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: `1px solid ${borderTint}`,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        transition: 'all 0.25s ease',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: `0 14px 32px ${hoverGlow}`,
          transform: 'translateY(-4px)',
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.secondary.main, 0.16),
            '& svg': {
              fontSize: 20,
              color: theme.palette.secondary.main,
            },
          }}
        >
          {icon}
        </Box>
        <Typography variant="overline" sx={{ color: 'primary.main' }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

const DestinationInsights: React.FC<{ 
  destinationInfo: any; 
  tips: string[]; 
  safetyNotes: string[]; 
  packingList: string[];
}> = ({ destinationInfo, tips, safetyNotes, packingList }) => {
  const theme = useTheme();
  const bulletColor = theme.palette.secondary.main;
  const bulletDotSize = 6;
  const bulletLineHeight = 1.7;
  const bulletFontSize = typeof theme.typography.body2?.fontSize === 'number'
    ? `${theme.typography.body2.fontSize}px`
    : theme.typography.body2?.fontSize || '1rem';
  const bulletOffset = `calc(((${bulletLineHeight}em - ${bulletDotSize}px) / 2) + 0.12em)`;

  const renderBullet = (content: string, index: number) => (
    <Stack
      direction="row"
      spacing={1.5}
      key={`${content}-${index}`}
      alignItems="flex-start"
      sx={{ fontSize: bulletFontSize }}
    >
      <Box
        sx={{
          width: bulletDotSize,
          height: bulletDotSize,
          borderRadius: '50%',
          backgroundColor: bulletColor,
          mt: bulletOffset,
          flexShrink: 0,
        }}
      />
      <MarkdownText
        variant="body2"
        sx={{ color: 'text.secondary', lineHeight: bulletLineHeight, fontSize: 'inherit' }}
      >
        {emphasizeMarkdownLead(content)}
      </MarkdownText>
    </Stack>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
        Destination Overview
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {destinationInfo.history && (
          <InfoCard title="History snapshot" icon={<HistoryRoundedIcon />}>
            <MarkdownText
              variant="body2"
              sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.7 }}
            >
              {emphasizeMarkdownLead(destinationInfo.history)}
            </MarkdownText>
          </InfoCard>
        )}

        {destinationInfo.culture && (
          <InfoCard title="Cultural pulse" icon={<MuseumRoundedIcon />}>
            <MarkdownText
              variant="body2"
              sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.7 }}
            >
              {emphasizeMarkdownLead(destinationInfo.culture)}
            </MarkdownText>
          </InfoCard>
        )}

        {destinationInfo.attractions && destinationInfo.attractions.length > 0 && (
          <InfoCard title="Must-see highlights" icon={<StarRoundedIcon />}>
            <Stack spacing={1.5}>
              {destinationInfo.attractions.map((attraction: string, index: number) => renderBullet(attraction, index))}
            </Stack>
          </InfoCard>
        )}

        {destinationInfo.interesting_facts && destinationInfo.interesting_facts.length > 0 && (
          <InfoCard title="Interesting facts" icon={<LightbulbRoundedIcon />}>
            <Stack spacing={1.5}>
              {destinationInfo.interesting_facts.map((fact: string, index: number) => renderBullet(fact, index))}
            </Stack>
          </InfoCard>
        )}

        {tips.length > 0 && (
          <InfoCard title="Travel tips" icon={<TipsAndUpdatesRoundedIcon />}>
            <Stack spacing={1.5}>
              {tips.map((item, index) => renderBullet(item, index))}
            </Stack>
          </InfoCard>
        )}

        {safetyNotes.length > 0 && (
          <InfoCard title="Safety notes" icon={<ShieldRoundedIcon />}>
            <Stack spacing={1.5}>
              {safetyNotes.map((item, index) => renderBullet(item, index))}
            </Stack>
          </InfoCard>
        )}

        {packingList.length > 0 && (
          <InfoCard title="Packing list" icon={<LuggageRoundedIcon />}>
            <Stack spacing={1.5}>
              {packingList.map((item, index) => renderBullet(item, index))}
            </Stack>
          </InfoCard>
        )}
      </Box>
    </Box>
  );
};

export const PlannerResultsPage: React.FC<PlannerResultsPageProps> = ({
  disableAutoRestore = false,
}) => {
  const {
    summary,
    destinationInfo,
    countryDestinationInfo,
    setDestinationInfo,
    setCountryDestinationInfo,
    sections,
    restaurants,
    specialActivities,
    weather,
    isWeatherLoading,
    tips,
    safetyNotes,
    packingList,
    toggleExperienceSelection,
    openExperience,
    activeExperience,
    travelData,
    isDestinationInfoLoading,
    loading,
    loadingPhase,
    hydrateFromTravelData,
    applySaveResult,
    currentSearchId,
    destinationImageUrls,
    isMultiCityMode,
    clearCurrentPlan,
  } = usePlanner();

  // For multi-city trips, use country destination info; otherwise use city destination info
  const resolvedCountryDestinationInfo = countryDestinationInfo ?? travelData?.country_destination_info ?? null;
  const isMultiCity =
    travelData?.plan_mode === 'multi_city' ||
    isMultiCityMode ||
    Boolean(resolvedCountryDestinationInfo) ||
    ((travelData?.cities?.length ?? 0) > 1);
  const displayDestinationInfo = isMultiCity
    ? resolvedCountryDestinationInfo
    : destinationInfo;
  const displaySafetyNotes = isMultiCity && resolvedCountryDestinationInfo?.safety_notes
    ? resolvedCountryDestinationInfo.safety_notes
    : safetyNotes;
  const displayPackingList = isMultiCity && resolvedCountryDestinationInfo?.packing_list
    ? resolvedCountryDestinationInfo.packing_list
    : packingList;
  const { searchId } = useParams<{ searchId?: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [sharedLoading, setSharedLoading] = React.useState(false);
  const [sharedError, setSharedError] = React.useState<string | null>(null);
  const [activeDayIndex, setActiveDayIndex] = React.useState(-1);
  const [addingDayId, setAddingDayId] = React.useState<string | null>(null);
  const [noPlanNoticeOpen, setNoPlanNoticeOpen] = React.useState(false);
  const [hasCachedPlanState, setHasCachedPlanState] = React.useState<boolean>(() => {
    try {
      return Boolean(localStorage.getItem('tripgen_planner_state'));
    } catch {
      return false;
    }
  });

  const [isAddingRestaurants, setIsAddingRestaurants] = React.useState(false);
  const [isGeneratingNewDay, setIsGeneratingNewDay] = React.useState(false);
  const [addActivitiesNotice, setAddActivitiesNotice] = React.useState<{ message: string; severity: 'warning' | 'error' } | null>(null);
  const [isMapFullscreen, setIsMapFullscreen] = React.useState(false);
  const [deletingActivities, setDeletingActivities] = React.useState<{ [key: string]: boolean }>({});
  const [restoringActivityId, setRestoringActivityId] = React.useState<string | null>(null);
  const [restoreMenuAnchor, setRestoreMenuAnchor] = React.useState<{ [key: string]: HTMLElement | null }>({});

  // Check if any operation is in progress
  const isAnyOperationInProgress = Boolean(addingDayId) || isAddingRestaurants || isGeneratingNewDay || Object.values(deletingActivities).some(Boolean);
  
  // Update PlannerContext with operation status
  const { setOperationInProgress } = usePlanner();
  React.useEffect(() => {
    setOperationInProgress(isAnyOperationInProgress);
  }, [isAnyOperationInProgress, setOperationInProgress]);

  React.useEffect(() => {
    if (disableAutoRestore) {
      return;
    }

    if (!searchId) {
      setSharedLoading(false);
      setSharedError(null);
      return;
    }

    // If this search is already loaded, don't restore again
    if (searchId === currentSearchId) {
      return;
    }

    let isActive = true;
    setSharedLoading(true);
    setSharedError(null);

    (async () => {
      try {
        const result = await restoreSearch(searchId);
        if (!isActive) {
          return;
        }

        if (result?.travel_state) {
          const restoredSearchId = result?.search_history?.search_id ?? searchId;
          const isMultiCity = result.travel_state?.plan_mode === 'multi_city' ||
                              (result.travel_state?.cities?.length ?? 0) > 1 ||
                              Boolean(result.multi_city_plan) ||
                              Boolean(result.country_destination_info || result.travel_state?.country_destination_info);

          // 🔍 [RESTORE] Debug: Log what we received from restoreSearch
          console.log('📦 [RESTORE] Received restoreSearch result:', {
            searchId,
            isMultiCity,
            hasTopLevelCountryInfo: !!result.country_destination_info,
            hasTopLevelDestinationInfo: !!result.destination_info,
            hasTravelStateCountryInfo: !!result.travel_state.country_destination_info,
            hasTravelStateDestinationInfo: !!result.travel_state.destination_info,
            planMode: result.travel_state?.plan_mode,
            citiesCount: result.travel_state?.cities?.length || 0,
          });

          // Prefer country info for multi-city; check both top-level and nested in travel_state
          // Check top-level first, then fall back to travel_state.country_destination_info
          const countryInfo = isMultiCity
            ? (result.country_destination_info || result.travel_state.country_destination_info || null)
            : null;
          const cityInfo = !isMultiCity
            ? (result.destination_info || result.travel_state.destination_info || null)
            : (countryInfo ? null : (result.destination_info || result.travel_state.destination_info || null));

          console.log('✅ [RESTORE] Extracted destination info:', {
            countryInfo: countryInfo ? { name: countryInfo.name, hasHistory: !!countryInfo.history } : null,
            cityInfo: cityInfo ? { name: cityInfo.name } : null,
            isMultiCity,
          });

          hydrateFromTravelData(result.travel_state, {
            merge: false,
            searchId: restoredSearchId,
            destinationInfo: cityInfo,
            countryDestinationInfo: countryInfo,
          });

          setSharedError(null);
        } else {
          setSharedError('Trip data is incomplete');
        }
      } catch (err: any) {
        if (!isActive) {
          return;
        }
        setSharedError(
          err?.message || 'Failed to load this trip. It may have been deleted or the link is invalid.',
        );
      } finally {
        if (isActive) {
          setSharedLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [disableAutoRestore, searchId, currentSearchId, hydrateFromTravelData, setDestinationInfo, setCountryDestinationInfo]);

  React.useEffect(() => {
    setActiveDayIndex(-1);
  }, [sections.length]);

  const handleShareTrip = React.useCallback(
    async (): Promise<string | null> => {
      if (!travelData) {
        return null;
      }

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));
        const result = await saveTrip(statePayload, currentSearchId || undefined);

        if (result?.searchId) {
          // Update context with new searchId BEFORE navigating to prevent restore loop
          hydrateFromTravelData(travelData, { merge: true, searchId: result.searchId });

          // Update URL with new search_id
          if (result.searchId !== searchId) {
            navigate(`/trip/${result.searchId}`, { replace: true });
          }

          return result.searchId;
        }
        return null;
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to save trip. Please try again.',
          severity: 'error',
        });
        return null;
      }
    },
    [travelData, currentSearchId, searchId, navigate, hydrateFromTravelData],
  );

  const hasSections = sections.length > 0;
  const totalDayCount = travelData?.total_trip_days ?? travelData?.travel_plan?.length ?? sections.length;
  const hasNoCurrentPlan =
    !sharedLoading &&
    !loading &&
    !travelData &&
    sections.length === 0 &&
    !currentSearchId &&
    !searchId &&
    !hasCachedPlanState;
  const hasMapContent = hasSections || restaurants.length > 0 || Boolean(travelData?.travel_plan?.length);
  const showMapLauncher = Boolean(travelData?.destination) && hasMapContent && !activeExperience;

  const shareAction = React.useMemo(
    () =>
      isMapFullscreen
        ? null
        : (
          <>
            <SaveTripButton
              label="Save"
              size={isCompactScreen ? 'medium' : 'small'}
              fullWidth={isCompactScreen}
              disabled={loading}
              onSave={handleShareTrip}
            />
            <ShareTripButton
              searchId={currentSearchId}
              label="Share"
              size={isCompactScreen ? 'medium' : 'small'}
              fullWidth={isCompactScreen}
              disabledLabel={loading ? 'Generating link…' : 'Link available once saved'}
              onShare={handleShareTrip}
            />
          </>
        ),
    [currentSearchId, isCompactScreen, isMapFullscreen, loading, handleShareTrip],
  );

  React.useEffect(() => {
    try {
      setHasCachedPlanState(Boolean(localStorage.getItem('tripgen_planner_state')));
    } catch {
      setHasCachedPlanState(false);
    }
  }, [travelData, currentSearchId, sections.length]);

  React.useEffect(() => {
    if (!sharedError) {
      return;
    }

    setNoPlanNoticeOpen(true);
    void clearCurrentPlan();
    setHasCachedPlanState(false);
  }, [sharedError, clearCurrentPlan]);

  React.useEffect(() => {
    if (!hasNoCurrentPlan) {
      return;
    }

    setNoPlanNoticeOpen(true);
    void clearCurrentPlan();
    setHasCachedPlanState(false);
  }, [hasNoCurrentPlan, clearCurrentPlan]);

  const handleNoticeClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoPlanNoticeOpen(false);
  };

  const handleAddSectionActivities = React.useCallback(
    async (section: PlannerSection) => {
      if (!travelData) {
        return;
      }

      const inferredDayNumber =
        typeof section.dayNumber === 'number'
          ? section.dayNumber
          : parseInt(section.id.replace('day-', ''), 10);

      if (!inferredDayNumber || Number.isNaN(inferredDayNumber)) {
        setAddActivitiesNotice({
          message: 'Could not determine which day to update.',
          severity: 'error',
        });
        return;
      }

      const preferredTypes = Array.isArray(travelData.preferred_activity_types)
        ? travelData.preferred_activity_types
        : [];
      const budget = travelData.budget || 'moderate';

      setAddingDayId(section.id);
      setAddActivitiesNotice(null);

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));
        const response = await addDayActivities(
          inferredDayNumber,
          statePayload,
          preferredTypes,
          budget,
          {
            searchId: currentSearchId ?? undefined,
          },
        );

        if (response?.state) {
          const savedSearchId = response.saveResult
            ? await applySaveResult(response.saveResult)
            : null;
          const nextSearchId = savedSearchId ?? response.saveResult?.search_id ?? currentSearchId ?? undefined;
          hydrateFromTravelData(response.state, { merge: true, searchId: nextSearchId });
          setActiveDayIndex(inferredDayNumber - 1);

          if (response.saveResult?.status === 'failed') {
            setAddActivitiesNotice({
              message:
                'New activities were added, but saving to your account failed. Your itinerary reflects the updates locally.',
              severity: 'warning',
            });
          }
        } else {
          setAddActivitiesNotice({
            message: 'We did not receive an updated itinerary. Please try again.',
            severity: 'error',
          });
        }
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to add activities. Please try again.',
          severity: 'error',
        });
      } finally {
        setAddingDayId(null);
      }
    },
    [travelData, currentSearchId, hydrateFromTravelData, applySaveResult, setActiveDayIndex],
  );

  const handleAddRestaurants = React.useCallback(
    async (section: PlannerSection) => {
      // Allow any restaurant section (including city-specific ones like 'restaurants-paris')
      const isRestaurantSection = section.id === 'restaurants' || section.id?.startsWith('restaurants-');
      if (!travelData || !isRestaurantSection) {
        return;
      }

      const budget = travelData.budget || 'moderate';

      setIsAddingRestaurants(true);
      setAddActivitiesNotice(null);

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));
        const response = await addRestaurants(
          statePayload,
          budget,
          {
            searchId: currentSearchId ?? undefined,
            additionalInfo: travelData?.user_additional_info ?? null,
            initialQuery: travelData?.initial_query ?? null,
          },
        );

        if (response?.state) {
          const savedSearchId = response.saveResult
            ? await applySaveResult(response.saveResult)
            : null;
          const nextSearchId = savedSearchId ?? response.saveResult?.search_id ?? currentSearchId ?? undefined;
          hydrateFromTravelData(response.state, { merge: true, searchId: nextSearchId });

          if (response.saveResult?.status === 'failed') {
            setAddActivitiesNotice({
              message:
                'New restaurants were added, but saving to your account failed. Your itinerary reflects the updates locally.',
              severity: 'warning',
            });
          }
        } else {
          setAddActivitiesNotice({
            message: 'We did not receive an updated itinerary. Please try again.',
            severity: 'error',
          });
        }
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to add restaurants. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsAddingRestaurants(false);
      }
    },
    [travelData, currentSearchId, hydrateFromTravelData, applySaveResult],
  );

  const handleDeleteActivity = React.useCallback(
    async (activityId: string) => {
      if (!travelData || isAnyOperationInProgress) {
        return;
      }

      setDeletingActivities(prev => ({ ...prev, [activityId]: true }));

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));
        const response = await deleteActivity(activityId, statePayload);

        if (response?.state) {
          const savedSearchId = response.saveResult
            ? await applySaveResult(response.saveResult)
            : null;
          const nextSearchId = savedSearchId ?? response.saveResult?.search_id ?? currentSearchId ?? undefined;
          hydrateFromTravelData(response.state, { merge: true, searchId: nextSearchId });

          // Update URL with new search_id if it changed
          if (nextSearchId && nextSearchId !== searchId) {
            navigate(`/trip/${nextSearchId}`, { replace: true });
          }

          if (response.saveResult?.status === 'failed') {
            setAddActivitiesNotice({
              message:
                'Activity was deleted, but saving to your account failed. Your itinerary reflects the updates locally.',
              severity: 'warning',
            });
          } else if (response.saveResult?.status === 'created') {
            setAddActivitiesNotice({
              message: 'Activity deleted and trip saved successfully.',
              severity: 'success' as any,
            });
          }
        } else {
          setAddActivitiesNotice({
            message: 'Failed to delete activity. Please try again.',
            severity: 'error',
          });
        }
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to delete activity. Please try again.',
          severity: 'error',
        });
      } finally {
        setDeletingActivities(prev => ({ ...prev, [activityId]: false }));
      }
    },
    [applySaveResult, currentSearchId, hydrateFromTravelData, travelData, isAnyOperationInProgress, searchId, navigate],
  );

  const handleRestoreActivity = React.useCallback(
    async (activityId: string, targetDayIndex?: number) => {
      if (!travelData || isAnyOperationInProgress || restoringActivityId) {
        return;
      }

      const isRestaurant = activityId.startsWith('rest-');

      setRestoringActivityId(activityId);
      setRestoreMenuAnchor(prev => ({ ...prev, [activityId]: null }));

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));

        // For restaurants, just remove from deleted_activities and add back to restaurants
        if (isRestaurant) {
          const deletedActivities = statePayload.deleted_activities || [];
          const restaurantToRestore = deletedActivities.find((a: any) => a.id === activityId);

          if (restaurantToRestore) {
            // Remove from deleted_activities
            statePayload.deleted_activities = deletedActivities.filter((a: any) => a.id !== activityId);

            // Add back to restaurants
            if (!statePayload.restaurants) {
              statePayload.restaurants = [];
            }
            statePayload.restaurants.push(restaurantToRestore);

            hydrateFromTravelData(statePayload, { merge: true, searchId: currentSearchId ?? undefined });
            setAddActivitiesNotice({
              message: 'Restaurant restored successfully.',
              severity: 'success' as any,
            });
          }
        } else {
          // For regular activities, use moveActivity API
          if (targetDayIndex === undefined) {
            throw new Error('Target day is required for activities');
          }

          const updatedState = await moveActivity(
            activityId,
            targetDayIndex,
            'morning',
            statePayload
          );

          if (updatedState) {
            hydrateFromTravelData(updatedState, { merge: true, searchId: currentSearchId ?? undefined });
            setAddActivitiesNotice({
              message: `Activity restored to Day ${targetDayIndex + 1} successfully.`,
              severity: 'success' as any,
            });
          } else {
            setAddActivitiesNotice({
              message: 'Failed to restore activity. Please try again.',
              severity: 'error',
            });
          }
        }
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to restore activity. Please try again.',
          severity: 'error',
        });
      } finally {
        setRestoringActivityId(null);
      }
    },
    [currentSearchId, hydrateFromTravelData, travelData, isAnyOperationInProgress, restoringActivityId],
  );

  const handleGenerateNewDay = React.useCallback(
    async () => {
      if (!travelData) {
        return;
      }

      const preferredTypes = Array.isArray(travelData.preferred_activity_types)
        ? travelData.preferred_activity_types
        : [];
      const budget = travelData.budget || 'moderate';

      setIsGeneratingNewDay(true);
      setAddActivitiesNotice(null);

      try {
        const statePayload = JSON.parse(JSON.stringify(travelData));
        const response = await generateNewDay(
          statePayload,
          preferredTypes,
          budget,
          {
            searchId: currentSearchId ?? undefined,
          },
        );

        if (response?.state) {
          const savedSearchId = response.saveResult
            ? await applySaveResult(response.saveResult)
            : null;
          const nextSearchId = savedSearchId ?? response.saveResult?.search_id ?? currentSearchId ?? undefined;
          hydrateFromTravelData(response.state, { merge: true, searchId: nextSearchId });

          if (response.saveResult?.status === 'failed') {
            setAddActivitiesNotice({
              message:
                'A new day was generated, but saving to your account failed. Your itinerary reflects the updates locally.',
              severity: 'warning',
            });
          }
        } else {
          setAddActivitiesNotice({
            message: 'We did not receive an updated itinerary. Please try again.',
            severity: 'error',
          });
        }
      } catch (error: any) {
        setAddActivitiesNotice({
          message: error?.message || 'Failed to generate a new day. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsGeneratingNewDay(false);
      }
    },
    [travelData, currentSearchId, hydrateFromTravelData, applySaveResult],
  );

  // Prepare loading states for the top indicator
  const loadingStates: LoadingState[] = React.useMemo(() => {
    const states: LoadingState[] = [];
    
    // Loading destination info - logical progression
    if (isDestinationInfoLoading) {
      states.push({
        isLoading: true,
        messages: [
          'Researching your destination...',
          'Exploring local culture and history...',
          'Gathering travel insights from locals...',
          'Finalizing destination highlights and recommendations...',
        ],
        variant: 'details',
      });
    }

    // Loading activities - logical progression
    if (loading && loadingPhase === 'activities_coordination') {
      states.push({
        isLoading: true,
        messages: [
          'Analyzing your travel preferences...',
          'Selecting unique experiences for you...',
          'Organizing activities into your perfect schedule...',
          'Finalizing your personalized travel plan...',
        ],
        variant: 'activities',
      });
    }

    if (loading && loadingPhase === 'activities_preliminary') {
      states.push({
        isLoading: true,
        messages: [
          'Fetching detailed activity information...',
          'Checking reviews and current availability...',
          'Verifying opening hours and accessibility...',
          'Adding insider tips and local recommendations...',
        ],
        variant: 'activities',
      });
    }

    if (addingDayId) {
      states.push({
        isLoading: true,
        messages: [
          'Curating fresh suggestions for your day...',
          'Matching new ideas with your preferences...',
          'Updating your itinerary with extra experiences...',
        ],
        variant: 'activities',
      });
    }

    if (isAddingRestaurants) {
      states.push({
        isLoading: true,
        messages: [
          'Discovering new dining spots...',
          'Finding the best local restaurants...',
          'Updating your restaurant recommendations...',
        ],
        variant: 'restaurants',
      });
    }

    if (isGeneratingNewDay) {
      states.push({
        isLoading: true,
        messages: [
          'Planning your new day...',
          'Discovering exciting activities and experiences...',
          'Creating the perfect itinerary for your extra day...',
        ],
        variant: 'activities',
      });
    }

    // Loading restaurants - show when activities are done but restaurants haven't loaded yet
    // This matches the exact condition used for the inline loading message
    const isWaitingForRestaurants = hasSections && restaurants.length === 0;
    
    if (isWaitingForRestaurants) {
      states.push({
        isLoading: true,
        messages: [
          'Discovering the best local restaurants...',
          'Finding authentic culinary experiences...',
        ],
        variant: 'restaurants',
      });
    }

    // General loading - logical progression
    if (loading && !loadingPhase) {
      states.push({
        isLoading: true,
        messages: [
          'Processing your travel request...',
          'Analyzing your preferences and interests...',
          'Creating your personalized travel plan...',
        ],
        variant: 'default',
      });
    }

    return states;
  }, [isDestinationInfoLoading, loading, loadingPhase, hasSections, restaurants.length, addingDayId, isAddingRestaurants, isGeneratingNewDay]);

  // Handler to open activity details when clicking on map pin
  // Use a ref to debounce rapid clicks
  const lastClickTimeRef = React.useRef<number>(0);
  const lastActivityIdRef = React.useRef<string | null>(null);
  
  const handleMapActivitySelect = React.useCallback((activityId: string) => {
    const now = Date.now();
    // Debounce: ignore clicks within 300ms of each other for the same activity
    if (activityId === lastActivityIdRef.current && now - lastClickTimeRef.current < 300) {
      console.log('⏭️ [PlannerResultsPage] Ignoring duplicate click:', activityId);
      return;
    }
    
    lastClickTimeRef.current = now;
    lastActivityIdRef.current = activityId;
    
    console.log('🔍 [PlannerResultsPage] handleMapActivitySelect called with activityId:', activityId);
    
    // Find the experience across all sections (day activities)
    for (const section of sections) {
      const experience = section.experiences.find((exp) => exp.id === activityId);
      if (experience) {
        console.log('✅ [PlannerResultsPage] Found experience in sections:', experience);
        openExperience(experience);
        return;
      }
    }
    // Also check restaurants
    const restaurant = restaurants.find((exp) => exp.id === activityId);
    if (restaurant) {
      console.log('✅ [PlannerResultsPage] Found restaurant:', restaurant);
      openExperience(restaurant);
      return;
    }
    console.warn('⚠️ [PlannerResultsPage] Activity not found for ID:', activityId);
  }, [sections, restaurants, openExperience]);

  const noPlanSnackbar = (
    <Snackbar
      open={noPlanNoticeOpen}
      autoHideDuration={6000}
      onClose={handleNoticeClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleNoticeClose} severity="info" sx={{ width: '100%' }}>
        No current trips are generated yet. Create a new plan to see it here.
      </Alert>
    </Snackbar>
  );

  if (sharedLoading) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main', mb: 2 }} size={48} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Loading shared trip...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1 }}>
            Just a moment while we prepare your itinerary
          </Typography>
        </Box>
        {noPlanSnackbar}
      </Box>
    );
  }

  if (sharedError) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            No current trips are available for this link. The plan may have been removed or never generated.
            {sharedError && (
              <Typography variant="body2" component="div" sx={{ color: 'text.secondary', mt: 1 }}>
                {sharedError}
              </Typography>
            )}
          </Alert>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Unable to load this trip
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            The trip you're looking for might have been deleted or the link may be invalid.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/')}>
              Create a New Trip
            </Button>
            <Button variant="outlined" onClick={() => navigate('/history')}>
              View My Trips
            </Button>
          </Box>
        </Box>
        {noPlanSnackbar}
      </Box>
    );
  }

  if (hasNoCurrentPlan) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 520 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            No current trips are generated yet. Create a new plan to see it here.
          </Alert>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Nothing to show just yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Start a trip from the planner or pick one from your history to make this page come alive.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/')}>
              Plan a Trip
            </Button>
            <Button variant="outlined" onClick={() => navigate('/history')}>
              View My Trips
            </Button>
          </Box>
        </Box>
        {noPlanSnackbar}
      </Box>
    );
  }

  return (
    <Box>
      <TopLoadingIndicator loadingStates={loadingStates} />
      <PlannerLayout
        overview={
          <TripOverviewHero
            summary={summary}
            destinationInfo={displayDestinationInfo}
            dayCount={totalDayCount}
            budget={travelData?.budget}
            actions={shareAction}
            destinationImageUrls={destinationImageUrls}
          />
        }
        weatherPanel={<WeatherSummaryCard weather={weather} isLoading={isWeatherLoading} />}
        destinationInsights={
          <>
            {isDestinationInfoLoading && (
              <Paper
                elevation={0}
                sx={(theme) => ({
                  p: 3,
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  backgroundColor: theme.palette.background.paper,
                })}
              >
                <Stack spacing={2}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Gathering destination insights
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    We are fetching history, culture, and travel tips while your travel plan is being composed.
                  </Typography>
                  <LinearProgress />
                </Stack>
              </Paper>
            )}

            {!isDestinationInfoLoading && displayDestinationInfo && (
              <>
                <DestinationInsights
                  destinationInfo={displayDestinationInfo}
                  tips={tips}
                  safetyNotes={displaySafetyNotes}
                  packingList={displayPackingList}
                />
                <MultiCityRouteNotes
                  cities={travelData?.cities}
                  isMultiCity={isMultiCity && (travelData?.cities?.length ?? 0) > 0}
                />
              </>
            )}
          </>
        }
        mainContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 6 } }}>
            {addActivitiesNotice && (
              <Alert
                severity={addActivitiesNotice.severity}
                onClose={() => setAddActivitiesNotice(null)}
              >
                {addActivitiesNotice.message}
              </Alert>
            )}

            {hasSections ? (
              sections.map((section, index) => {
                const isOverflowSection = section.id === 'day-overflow';
                return (
                  <ExperienceSection
                    key={section.id}
                    section={section}
                    onToggleSelect={toggleExperienceSelection}
                    onOpenDetails={openExperience}
                    onFocusSection={isOverflowSection ? undefined : () => setActiveDayIndex(index)}
                    onAddActivities={
                      isOverflowSection ? undefined : () => handleAddSectionActivities(section)
                    }
                    isAddingActivities={addingDayId === section.id}
                    isAnyOperationInProgress={isAnyOperationInProgress}
                    onDeleteActivity={handleDeleteActivity}
                    deletingActivities={deletingActivities}
                  />
                );
              })
            ) : (
              <PlannerLoadingPlaceholder phase={loadingPhase} />
            )}

            {Object.keys(specialActivities || {}).length > 0 && (
              <Box>
                <Divider sx={{ my: 4 }} />
                <SpecialActivitiesSection activitiesByDay={specialActivities} />
              </Box>
            )}

            {/* Show loading state while restaurants are being generated */}
            {hasSections && restaurants.length === 0 && (
              <Box>
                <Divider sx={{ my: 4 }} />
                <PlannerLoadingPlaceholder variant="restaurants" />
              </Box>
            )}

            {travelData?.restaurants && travelData.restaurants.length > 0 && restaurants.length > 0 && (
              <Box>
                <Divider sx={{ my: 4 }} />
                <Typography variant="h4" sx={{ mb: 2 }}>
                  Dining picks
                </Typography>
                {(() => {
                  // Group restaurants by city for multi-city trips


                                    // Helper to normalize keys for matching
                                    const normalizeKey = (k: string | undefined) => k?.toLowerCase().trim();
                  
                                    // First pass: group all restaurants by city key or name
                                    const restaurantsByCity = new Map<string, typeof restaurants>();
                                    const foundCityInfos = new Map<string, { name: string, key: string }>();
                  
                                    restaurants.forEach((restaurant: any) => {
                                      const rawKey = restaurant.city_key || restaurant.city || 'other';
                                      const key = normalizeKey(rawKey) || 'other';
                                      
                                      if (!restaurantsByCity.has(key)) {
                                        restaurantsByCity.set(key, []);
                                        if (key !== 'other') {
                                           foundCityInfos.set(key, { 
                                             name: restaurant.city || restaurant.city_key, 
                                             key: rawKey 
                                           });
                                        }
                                      }
                                      restaurantsByCity.get(key)!.push(restaurant);
                                    });
                  
                                    // Determine if we should display as grouped cities
                                    // We group if isMultiCity is true OR if we found more than 1 distinct city (excluding "other" if it's the only one)
                                    const distinctCitiesCount = foundCityInfos.size;
                                    const shouldGroup = isMultiCity || distinctCitiesCount > 1;
                  
                                    if (shouldGroup) {
                                      // determine the order of cities to display
                                      let displayCities: Array<{ city: string, city_key: string }> = [];
                                      
                                      if (travelData.cities && travelData.cities.length > 0) {
                                          displayCities = travelData.cities.map(c => ({
                                              city: c.city,
                                              city_key: c.city_key
                                          }));
                                      } else {
                                          // Fallback: use cities found in restaurants
                                           displayCities = Array.from(foundCityInfos.values()).map(info => ({
                                               city: info.name,
                                               city_key: info.key
                                           }));
                                      }
                  
                                      // If we have cities to display (or at least "other" bucket has content), render them
                                      // If displayCities is empty but we have "other" bucket, we should render "other"
                                      
                                      if (displayCities.length > 0 || (restaurantsByCity.has('other') && restaurantsByCity.get('other')!.length > 0)) {
                                          return (
                                               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                  {displayCities.map((city, index) => {
                                                     // Try exact match on key
                                                     let matchKey = normalizeKey(city.city_key);
                                                     let cityRestaurants = matchKey ? restaurantsByCity.get(matchKey) : undefined;
                                                     
                                                     // If no match, try match on city name
                                                     if (!cityRestaurants) {
                                                         matchKey = normalizeKey(city.city);
                                                         if (matchKey) {
                                                             cityRestaurants = restaurantsByCity.get(matchKey);
                                                         }
                                                     }
                  
                                                     if (!cityRestaurants || cityRestaurants.length === 0) return null;
                  
                                                     const citySection = (
                                                       <Box key={city.city_key || city.city}>
                                                         <ExperienceSection
                                                           section={{
                                                             id: `restaurants-${city.city_key || city.city}`,
                                                             title: `Where to eat in ${city.city}`,
                                                             subtitle: `Hand-picked places to taste the destination in ${city.city}`,
                                                             experiences: cityRestaurants,
                                                           }}
                                                           onToggleSelect={toggleExperienceSelection}
                                                           onOpenDetails={openExperience}
                                                           onAddActivities={handleAddRestaurants}
                                                           isAddingActivities={isAddingRestaurants}
                                                           isAnyOperationInProgress={isAnyOperationInProgress}
                                                           onDeleteActivity={handleDeleteActivity}
                                                           deletingActivities={deletingActivities}
                                                         />
                                                       </Box>
                                                     );
                  
                                                     return citySection;
                                                  })
                                                  .filter(Boolean)
                                                  .map((section, idx, arr) => (
                                                     <React.Fragment key={`city-section-${idx}`}>
                                                       {section}
                                                       {idx < arr.length - 1 && <Divider sx={{ my: 4 }} />}
                                                     </React.Fragment>
                                                  ))}
                  
                                                  {/* Show restaurants without city_key in a separate section */}
                                                  {restaurantsByCity.has('other') && restaurantsByCity.get('other')!.length > 0 && (
                                                    <React.Fragment>
                                                      {displayCities.length > 0 && <Divider sx={{ my: 4 }} />}
                                                      <Box>
                                                        <ExperienceSection
                                                          section={{
                                                            id: 'restaurants-other',
                                                            title: 'Where to eat',
                                                            subtitle: 'Hand-picked places to taste the destination',
                                                            experiences: restaurantsByCity.get('other')! as any[],
                                                          }}
                                                          onToggleSelect={toggleExperienceSelection}
                                                           onOpenDetails={openExperience}
                                                           onAddActivities={handleAddRestaurants}
                                                           isAddingActivities={isAddingRestaurants}
                                                           isAnyOperationInProgress={isAnyOperationInProgress}
                                                           onDeleteActivity={handleDeleteActivity}
                                                           deletingActivities={deletingActivities}
                                                         />
                                                       </Box>
                                                     </React.Fragment>
                                                  )}
                                               </Box>
                                          );
                                      }
                                    }                  
                  // Single city trip - display all restaurants in one section
                  return (
                    <ExperienceSection
                      section={{
                        id: 'restaurants',
                        title: 'Where to eat',
                        subtitle: 'Hand-picked places to taste the destination',
                        experiences: restaurants,
                      }}
                      onToggleSelect={toggleExperienceSelection}
                      onOpenDetails={openExperience}
                      onAddActivities={handleAddRestaurants}
                      isAddingActivities={isAddingRestaurants}
                      isAnyOperationInProgress={isAnyOperationInProgress}
                      onDeleteActivity={handleDeleteActivity}
                      deletingActivities={deletingActivities}
                    />
                  );
                })()}
              </Box>
            )}

            {hasSections && !loading && !isGeneratingNewDay && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ my: 4 }} />
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: { xs: 2.5, md: 3.5 },
                    borderRadius: 4,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    background: `
                      linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.035)} 52%, ${alpha(PERIPLO_BRAND.main, 0.06)} 100%)
                    `,
                    boxShadow: `0 22px 54px ${alpha(theme.palette.common.black, 0.06)}`,
                    overflow: 'hidden',
                  })}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: '1.1fr 0.9fr' },
                      gap: { xs: 3, lg: 0 },
                      alignItems: 'stretch',
                    }}
                  >
                    <Stack
                      spacing={2.25}
                      alignItems="flex-start"
                      sx={{ pr: { lg: 3.5 } }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={(theme) => ({
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.palette.common.white,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 14px 28px ${alpha(theme.palette.primary.main, 0.2)}`,
                          })}
                        >
                          <AddCircleOutlineRoundedIcon sx={{ fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            Extend this itinerary
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                            Add another day to the trip
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 560 }}>
                        Generate a fresh day of activities that fits the destination, trip pacing, and the plan you already have.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddCircleOutlineRoundedIcon />}
                        onClick={handleGenerateNewDay}
                        disabled={isAnyOperationInProgress}
                      >
                        Generate another day
                      </Button>
                    </Stack>

                    <Stack
                      spacing={2.25}
                      alignItems="flex-start"
                      sx={(theme) => ({
                        pl: { lg: 3.5 },
                        pt: { xs: 3, lg: 0 },
                        borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.8)}`, lg: 'none' },
                        borderLeft: { xs: 'none', lg: `1px solid ${alpha(PERIPLO_BRAND.main, 0.18)}` },
                      })}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          component="img"
                          src={periploIcon}
                          alt="Periplo"
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '24%',
                            boxShadow: `0 10px 24px ${alpha(PERIPLO_BRAND.main, 0.24)}`,
                          }}
                        />
                        <Box>
                          <Typography variant="overline" sx={{ color: PERIPLO_BRAND.main, fontWeight: 700 }}>
                            New TripGen
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                            Continue in Periplo
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 560 }}>
                        Open the newer planning workspace for faster itinerary building and richer destination exploration.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        component="a"
                        href={PERIPLO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{
                          backgroundColor: PERIPLO_BRAND.main,
                          '&:hover': { backgroundColor: PERIPLO_BRAND.dark },
                        }}
                      >
                        Try Periplo
                      </Button>
                    </Stack>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Deleted Activities Section */}
            {((travelData as any)?.deleted_activities?.length ?? 0) > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.error.main, 0.16),
                      }}
                    >
                      <DeleteIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Deleted Activities
                    </Typography>
                    <Chip
                      label={(travelData as any).deleted_activities.length}
                      size="small"
                      sx={{
                        backgroundColor: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                        fontWeight: 600
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {(travelData as any).deleted_activities.length} {(travelData as any).deleted_activities.length === 1 ? 'item has' : 'items have'} been removed from your itinerary. Click restore to add them back.
                  </Typography>

                  {/* Deleted Regular Activities */}
                  {(() => {
                    const deletedRegularActivities = ((travelData as any).deleted_activities || []).filter((a: any) => !a.id.startsWith('rest-'));
                    if (deletedRegularActivities.length === 0) return null;

                    return (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, color: 'text.primary' }}>
                          Activities
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {deletedRegularActivities.map((activity: any) => (
                            <Box
                              key={activity.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                backgroundColor: theme.palette.background.paper,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {activity.name || 'Unnamed activity'}
                                </Typography>
                                {activity.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {Array.isArray(activity.category) ? activity.category.join(', ') : activity.category}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={restoringActivityId === activity.id ? <CircularProgress size={16} /> : <RestoreIcon />}
                                disabled={restoringActivityId !== null || isAnyOperationInProgress}
                                onClick={(e) => setRestoreMenuAnchor(prev => ({ ...prev, [activity.id]: e.currentTarget }))}
                                sx={{
                                  borderColor: theme.palette.success.main,
                                  color: theme.palette.success.main,
                                  '&:hover': {
                                    borderColor: theme.palette.success.dark,
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  }
                                }}
                              >
                                {restoringActivityId === activity.id ? 'Restoring...' : 'Restore'}
                              </Button>
                              <Menu
                                anchorEl={restoreMenuAnchor[activity.id]}
                                open={Boolean(restoreMenuAnchor[activity.id])}
                                onClose={() => setRestoreMenuAnchor(prev => ({ ...prev, [activity.id]: null }))}
                              >
                                {travelData?.travel_plan?.map((day: any, index: number) => (
                                  <MenuItem
                                    key={index}
                                    onClick={() => handleRestoreActivity(activity.id, index)}
                                  >
                                    Day {index + 1} - {day.date || `Day ${index + 1}`}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </Box>
                          ))}
                        </Box>
                      </>
                    );
                  })()}

                  {/* Deleted Restaurants */}
                  {(() => {
                    const deletedRestaurants = ((travelData as any).deleted_activities || []).filter((a: any) => a.id.startsWith('rest-'));
                    if (deletedRestaurants.length === 0) return null;

                    return (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, color: 'text.primary' }}>
                          Restaurants
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {deletedRestaurants.map((restaurant: any) => (
                            <Box
                              key={restaurant.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                backgroundColor: theme.palette.background.paper,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {restaurant.name || 'Unnamed restaurant'}
                                </Typography>
                                {restaurant.category && (
                                  <Typography variant="caption" color="text.secondary">
                                    {Array.isArray(restaurant.category) ? restaurant.category.join(', ') : restaurant.category}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={restoringActivityId === restaurant.id ? <CircularProgress size={16} /> : <RestoreIcon />}
                                disabled={restoringActivityId !== null || isAnyOperationInProgress}
                                onClick={() => handleRestoreActivity(restaurant.id)}
                                sx={{
                                  borderColor: theme.palette.success.main,
                                  color: theme.palette.success.main,
                                  '&:hover': {
                                    borderColor: theme.palette.success.dark,
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  }
                                }}
                              >
                                {restoringActivityId === restaurant.id ? 'Restoring...' : 'Restore'}
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </>
                    );
                  })()}
                </Stack>
              </Paper>
            )}
          </Box>
        }
        sidePanel={
          <PlannerMapPanel
            sections={sections}
            activeDayIndex={activeDayIndex}
            onDayChange={setActiveDayIndex}
            travelData={travelData}
            onActivitySelect={handleMapActivitySelect}
            restaurants={restaurants}
            isFullscreen={isMapFullscreen}
            onFullscreenChange={setIsMapFullscreen}
            headerTransparent={false}
            stickyOffset={isMapFullscreen ? 12 : undefined}
          />
        }
      />

      {showMapLauncher && (
        <IconButton
          color="primary"
          aria-label={isMapFullscreen ? 'Close map' : 'Expand map'}
          onClick={() => setIsMapFullscreen((prev) => !prev)}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, md: 32 },
            left: { xs: 20, md: 32 },
            zIndex: 1450,
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            boxShadow: '0 18px 28px rgba(0,0,0,0.16)',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            'body.assistant-open &': { display: 'none' },
            '& .MuiSvgIcon-root': { fontSize: { xs: 26, md: 30 } },
            '&:hover': {
              bgcolor: 'background.paper',
              transform: 'translateY(-1px)',
            },
          }}
        >
          <MapRoundedIcon />
        </IconButton>
      )}

      <ExperienceDetailDrawer
        experience={activeExperience}
        open={Boolean(activeExperience)}
        onClose={() => openExperience(null)}
        destination={travelData?.destination}
        onOpenExperience={openExperience}
        onDelete={handleDeleteActivity}
        isDeleting={activeExperience ? deletingActivities[activeExperience.id] : false}
      />
      {noPlanSnackbar}
    </Box>
  );
};
